import { useEffect, useId, useRef, useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { cn } from '@/utils/cn';
import { CalendarPopover } from '@/components/ui/CalendarPopover';
import { FieldLabel, parseFieldLabel } from '@/components/ui/FieldLabel';
import {
  displayDate,
  formatDateValue,
  parseDateValue,
} from '@/utils/dateInput';

export interface DatePickerProps {
  label?: string;
  error?: string;
  description?: string;
  hint?: string;
  value?: string;
  onChange?: (value: string) => void;
  min?: string;
  max?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  name?: string;
}

export function DatePicker({
  label,
  error,
  description: _description,
  hint: _hint,
  value = '',
  onChange,
  min,
  max,
  disabled,
  required,
  placeholder = 'Select date',
  className,
  id,
  name,
}: DatePickerProps) {
  const autoId = useId();
  const fieldId = id ?? name ?? autoId;
  const { required: isRequired } = parseFieldLabel(label, required);
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => parseDateValue(value) ?? new Date());

  useEffect(() => {
    const parsed = parseDateValue(value);
    if (parsed) setViewMonth(parsed);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pick = (date: Date) => {
    onChange?.(formatDateValue(date));
    setOpen(false);
  };

  const display = displayDate(value);

  return (
    <div className={cn('relative min-w-0 w-full', className)} ref={rootRef}>
      <FieldLabel htmlFor={fieldId} label={label} required={required} />
      <button
        type="button"
        id={fieldId}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={cn(
          'input flex w-full min-w-0 items-center gap-2 text-left',
          !display && 'text-slate-500 dark:text-slate-400',
          display && 'text-slate-900 dark:text-white',
          error && 'input-error',
          disabled && 'cursor-not-allowed opacity-60',
        )}
      >
        <CalendarTodayIcon className="shrink-0 text-primary-600 dark:text-primary-400" sx={{ fontSize: 18 }} />
        <span className="truncate">{display || placeholder}</span>
      </button>
      {name && (
        <input type="hidden" name={name} value={value} required={isRequired} readOnly />
      )}
      {open && (
        <CalendarPopover
          className="absolute left-0 top-full z-50 mt-1 w-[min(18rem,calc(100vw-2rem))]"
          value={value}
          viewMonth={viewMonth}
          onViewMonthChange={setViewMonth}
          onSelect={pick}
          min={min}
          max={max}
        />
      )}
      {error && (
        <p className="mt-1.5 text-xs text-error-700 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
